import React, { useEffect, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import Navbar from "./Navbar";
import Header from "./header";
import Dialog from "@mui/material/Dialog";
// import Swal from "sweetalert2";
import { toast } from "react-toastify";
import ApiConfig from "../../common/config/ApiConfig";
import Switch from "@mui/material/Switch";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
// import DeleteIcon from "@mui/icons-material/Delete";
import { useLanguage } from "../../common/i18n/LanguageContext";

// The server's app_schedule.ts jobHandlers map only recognizes these 12 keys
// today — a row with any other jobKey is accepted but will never fire. Create
// only offers a jobKey that doesn't already have a row, since this screen is
// for (re)timing the 12 fixed jobs, not inventing new ones. Not every job is
// a lottery result-fetcher — clear_activity_log is housekeeping (deletes old
// ActivityLog audit rows).
const KNOWN_JOB_KEYS = [
  { value: "th_gov", labelKey: "schedule_job_label_th_gov" },
  { value: "lao_gov", labelKey: "schedule_job_label_lao_gov" },
  { value: "yeekee5", labelKey: "schedule_job_label_yeekee5" },
  { value: "yeekee10", labelKey: "schedule_job_label_yeekee10" },
  { value: "yeekee15", labelKey: "schedule_job_label_yeekee15" },
  {
    value: "hanoy_PhuQua_ONE",
    labelKey: "schedule_job_label_hanoi_round_1",
  },
  {
    value: "hanoy_PhuQua_TWO",
    labelKey: "schedule_job_label_hanoi_round_2",
  },
  {
    value: "hanoy_PhuQua_THREE",
    labelKey: "schedule_job_label_hanoi_round_3",
  },
  {
    value: "hanoy_PhuQua_FOUR",
    labelKey: "schedule_job_label_hanoi_round_4",
  },
  {
    value: "hanoy_PhuQua_FIVE",
    labelKey: "schedule_job_label_hanoi_round_5",
  },
  {
    value: "hanoy_PhuQua_SIX",
    labelKey: "schedule_job_label_hanoi_round_6",
  },
  {
    value: "clear_activity_log",
    labelKey: "schedule_job_label_clear_activity_log",
  },
];

// Every job is edited through one of these typed shapes, not a raw cron
// string — the server derives cronExpression from scheduleType + config.
const SCHEDULE_TYPE_OPTIONS = [
  { value: "minute_list", labelKey: "schedule_type_minute_list" },
  { value: "daily", labelKey: "schedule_type_daily" },
  { value: "weekly_window", labelKey: "schedule_type_weekly_window" },
  { value: "advanced", labelKey: "schedule_type_advanced" },
];

const WEEKDAY_LABEL_KEYS = [
  "weekday_sun",
  "weekday_mon",
  "weekday_tue",
  "weekday_wed",
  "weekday_thu",
  "weekday_fri",
  "weekday_sat",
];

const MINUTES = Array.from({ length: 60 }, (_, i) => i);
const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => i + 1);
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const emptyForm = {
  id: null,
  jobKey: "",
  description: "",
  scheduleType: "minute_list",
  minutes: [],
  hour: "",
  daysOfMonth: [],
  dailyHour: 0,
  dailyMinute: 0,
  weekHour: 0,
  minuteStart: 0,
  minuteEnd: 59,
  daysOfWeek: [],
  cronExpression: "",
  enabled: true,
};

const toggleInArray = (arr, value) =>
  arr.includes(value)
    ? arr.filter((v) => v !== value)
    : [...arr, value].sort((a, b) => a - b);

const describeScheduleType = (type, t) => {
  const option = SCHEDULE_TYPE_OPTIONS.find((o) => o.value === type);
  return option ? t(option.labelKey) : type;
};

function Schedule() {
  const { t } = useLanguage();
  const pos = localStorage.getItem("position");
  const [schedules, setSchedules] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const fetchSchedules = () => {
    ApiConfig.get("/getScheduleList").then((result) => {
      if (result.resCode !== 200) {
        toast.error(result.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      setSchedules(Array.isArray(result.data) ? result.data : []);
    });
  };

  useEffect(() => {
    fetchSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const availableJobKeys = KNOWN_JOB_KEYS.filter(
    (jk) => !schedules.some((s) => s.jobKey === jk.value),
  );

  const openCreate = () => {
    if (availableJobKeys.length === 0) {
      toast.error(
        `${t("schedule_all_jobs_configured_prefix")} ${
          KNOWN_JOB_KEYS.length
        } ${t("schedule_all_jobs_configured_suffix")}`,
        {
          position: "top-right",
          autoClose: 3000,
        },
      );
      return;
    }
    setIsCreating(true);
    setForm({ ...emptyForm, jobKey: availableJobKeys[0].value });
    setDialogOpen(true);
  };

  const openEdit = (schedule) => {
    const config = schedule.config || {};
    const scheduleType = schedule.scheduleType || "minute_list";
    setIsCreating(false);
    setForm({
      ...emptyForm,
      id: schedule.id,
      jobKey: schedule.jobKey,
      description: schedule.description || "",
      scheduleType,
      enabled: !!schedule.enabled,
      minutes: scheduleType === "minute_list" ? config.minutes || [] : [],
      hour:
        scheduleType === "minute_list"
          ? config.hour == null
            ? ""
            : String(config.hour)
          : "",
      daysOfMonth:
        scheduleType === "minute_list" ? config.daysOfMonth || [] : [],
      dailyHour: scheduleType === "daily" ? (config.hour ?? 0) : 0,
      dailyMinute: scheduleType === "daily" ? (config.minute ?? 0) : 0,
      weekHour: scheduleType === "weekly_window" ? (config.hour ?? 0) : 0,
      minuteStart:
        scheduleType === "weekly_window" ? (config.minuteStart ?? 0) : 0,
      minuteEnd:
        scheduleType === "weekly_window" ? (config.minuteEnd ?? 59) : 59,
      daysOfWeek:
        scheduleType === "weekly_window" ? config.daysOfWeek || [] : [],
      cronExpression:
        scheduleType === "advanced" ? schedule.cronExpression || "" : "",
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setForm(emptyForm);
  };

  const buildPayload = () => {
    if (!form.description.trim()) {
      toast.error(t("schedule_description_required"), {
        position: "top-right",
        autoClose: 3000,
      });
      return null;
    }

    const base = {
      description: form.description.trim(),
      scheduleType: form.scheduleType,
      enabled: form.enabled,
    };

    if (form.scheduleType === "minute_list") {
      if (form.minutes.length === 0) {
        toast.error(t("schedule_minute_required"), {
          position: "top-right",
          autoClose: 3000,
        });
        return null;
      }
      return {
        ...base,
        config: {
          minutes: form.minutes,
          hour: form.hour === "" ? null : Number(form.hour),
          daysOfMonth: form.daysOfMonth.length ? form.daysOfMonth : null,
        },
      };
    }

    if (form.scheduleType === "daily") {
      return {
        ...base,
        config: {
          hour: Number(form.dailyHour),
          minute: Number(form.dailyMinute),
        },
      };
    }

    if (form.scheduleType === "weekly_window") {
      if (form.daysOfWeek.length === 0) {
        toast.error(t("schedule_day_required"), {
          position: "top-right",
          autoClose: 3000,
        });
        return null;
      }
      return {
        ...base,
        config: {
          hour: Number(form.weekHour),
          minuteStart: Number(form.minuteStart),
          minuteEnd: Number(form.minuteEnd),
          daysOfWeek: form.daysOfWeek,
        },
      };
    }

    // advanced
    if (!form.cronExpression.trim()) {
      toast.error(t("schedule_cron_required"), {
        position: "top-right",
        autoClose: 3000,
      });
      return null;
    }
    return { ...base, cronExpression: form.cronExpression.trim() };
  };

  const handleSubmit = () => {
    const payload = buildPayload();
    if (!payload) return;

    if (isCreating) {
      ApiConfig.post("/createSchedule", {
        jobKey: form.jobKey,
        ...payload,
      }).then((res) => {
        if (res.resCode !== 200) {
          toast.error(res.message || t("schedule_create_error"), {
            position: "top-right",
            autoClose: 3000,
          });
          return;
        }
        toast.success(t("schedule_create_success"), { autoClose: 2000 });
        closeDialog();
        fetchSchedules();
      });
      return;
    }

    ApiConfig.post("/updateSchedule", { id: form.id, ...payload }).then(
      (res) => {
        if (res.resCode !== 200) {
          toast.error(res.message || t("schedule_edit_error"), {
            position: "top-right",
            autoClose: 3000,
          });
          return;
        }
        toast.success(t("schedule_edit_success"), { autoClose: 2000 });
        closeDialog();
        fetchSchedules();
      },
    );
  };

  const handleToggleEnabled = (schedule, checked) => {
    ApiConfig.post("/updateSchedule", {
      id: schedule.id,
      enabled: checked,
    }).then((res) => {
      if (res.resCode !== 200) {
        toast.error(res.message || t("schedule_toggle_status_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      fetchSchedules();
    });
  };

  // const handleDelete = (schedule) => {
  //   Swal.fire({
  //     title: "แน่ใจหรือไม่?",
  //     text: `ต้องการลบตารางเวลา "${schedule.description}" ใช่หรือไม่? งานนี้จะไม่ทำงานอีก`,
  //     icon: "warning",
  //     showCancelButton: true,
  //     confirmButtonText: "ใช่, ลบ",
  //     cancelButtonText: "ยกเลิก",
  //   }).then((result) => {
  //     if (!result.isConfirmed) return;

  //     ApiConfig.post("/deleteSchedule", { id: schedule.id }).then((res) => {
  //       if (res.resCode !== 200) {
  //         toast.error(res.message || "ลบตารางเวลาไม่สำเร็จ", {
  //           position: "top-right",
  //           autoClose: 3000,
  //         });
  //         return;
  //       }
  //       toast.success("ลบตารางเวลาสำเร็จ", { autoClose: 2000 });
  //       fetchSchedules();
  //     });
  //   });
  // };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <div className="shrink-0">
        <Navbar />
      </div>
      <div className="flex flex-1 overflow-hidden items-start">
        <div className="shrink-0 overflow-y-auto h-full">
          <Sidebar pos={pos} />
        </div>
        <div className="flex flex-col flex-1 max-h-full overflow-hidden">
          <div className="shrink-0">
            <Header title={t("menu_schedule_setting")} />
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            <div className="flex items-center justify-end bg-white p-2 rounded-lg shadow-sm">
              <button
                onClick={openCreate}
                className="flex items-center gap-1 bg-brand-blue-600 hover:bg-brand-blue-700 text-white text-sm font-bold px-3 py-1 rounded"
              >
                <AddIcon style={{ fontSize: 18 }} />
                {t("schedule_add_button")}
              </button>
            </div>

            <div className="overflow-x-auto rounded bg-white shadow-sm">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-brand-blue-800 text-white">
                    <th className="px-3 py-2 text-center">
                      {t("membertable_col_no")}
                    </th>
                    {/* <th className="px-3 py-2 text-left">Job Key</th> */}
                    <th className="px-3 py-2 text-left">
                      {t("schedule_col_description")}
                    </th>
                    <th className="px-3 py-2 text-left">
                      {t("schedule_col_format")}
                    </th>
                    <th className="px-3 py-2 text-left">
                      {t("schedule_col_cron_example")}
                    </th>
                    <th className="px-3 py-2 text-center">
                      {t("schedule_enabled_label")}
                    </th>
                    <th className="px-3 py-2 text-center">
                      {t("membertable_col_manage")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-center py-4 text-gray-400"
                      >
                        {t("schedule_no_schedules_yet")}
                      </td>
                    </tr>
                  ) : (
                    schedules.map((schedule, index) => (
                      <tr
                        key={schedule.id}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-3 py-2 text-center text-gray-500">
                          {index + 1}
                        </td>
                        {/* <td className="px-3 py-2">
                          <span className="font-bold text-slate-700">
                            {schedule.jobKey}
                          </span>
                        </td> */}
                        <td className="px-3 py-2 text-slate-600">
                          {schedule.description}
                        </td>
                        <td className="px-3 py-2">
                          <span className="text-[11px] bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                            {describeScheduleType(schedule.scheduleType, t)}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <code className="bg-slate-100 px-2 py-0.5 rounded text-[12px]">
                            {schedule.cronExpression}
                          </code>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Switch
                            checked={!!schedule.enabled}
                            onChange={(e) =>
                              handleToggleEnabled(schedule, e.target.checked)
                            }
                            inputProps={{ "aria-label": "controlled" }}
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => openEdit(schedule)}
                              className="flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white text-[12px] font-bold px-2 py-1 rounded"
                            >
                              <EditIcon style={{ fontSize: 16 }} />
                              {t("common_edit")}
                            </button>
                            {/* <button
                              onClick={() => handleDelete(schedule)}
                              className="flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white text-[12px] font-bold px-2 py-1 rounded"
                            >
                              <DeleteIcon style={{ fontSize: 16 }} />
                              ลบ
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <div className="p-4 space-y-3">
          <h2 className="text-lg font-bold text-brand-blue-700">
            {isCreating
              ? t("schedule_add_button")
              : `${t("schedule_edit_title")} (${form.jobKey})`}
          </h2>

          {isCreating && (
            <div className="flex flex-col gap-1">
              <label className="text-sm font-semibold">Job Key</label>
              <select
                value={form.jobKey}
                onChange={(e) =>
                  setForm((f) => ({ ...f, jobKey: e.target.value }))
                }
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                {availableJobKeys.map((jk) => (
                  <option key={jk.value} value={jk.value}>
                    {t(jk.labelKey)} ({jk.value})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold">
              {t("schedule_col_description")}
            </label>
            <input
              type="text"
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-semibold">
              {t("schedule_type_label")}
            </label>
            <select
              value={form.scheduleType}
              onChange={(e) =>
                setForm((f) => ({ ...f, scheduleType: e.target.value }))
              }
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            >
              {SCHEDULE_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {t(o.labelKey)}
                </option>
              ))}
            </select>
          </div>

          {form.scheduleType === "minute_list" && (
            <div className="space-y-2 border-t border-slate-200 pt-2">
              <div>
                <label className="text-sm font-semibold block mb-1">
                  {t("schedule_minutes_label")}
                </label>
                <div className="grid grid-cols-10 gap-1 max-h-32 overflow-y-auto">
                  {MINUTES.map((m) => (
                    <button
                      type="button"
                      key={m}
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          minutes: toggleInArray(f.minutes, m),
                        }))
                      }
                      className={`text-[11px] rounded px-1 py-1 ${
                        form.minutes.includes(m)
                          ? "bg-brand-blue-600 text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-sm font-semibold">
                  {t("schedule_hour_optional_label")}
                </label>
                <select
                  value={form.hour}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, hour: e.target.value }))
                  }
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value="">{t("schedule_every_hour")}</option>
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-semibold block mb-1">
                  {t("schedule_days_of_month_optional_label")}
                </label>
                <div className="grid grid-cols-10 gap-1 max-h-32 overflow-y-auto">
                  {DAYS_OF_MONTH.map((d) => (
                    <button
                      type="button"
                      key={d}
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          daysOfMonth: toggleInArray(f.daysOfMonth, d),
                        }))
                      }
                      className={`text-[11px] rounded px-1 py-1 ${
                        form.daysOfMonth.includes(d)
                          ? "bg-brand-blue-600 text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {form.scheduleType === "daily" && (
            <div className="flex gap-2 border-t border-slate-200 pt-2">
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-sm font-semibold">
                  {t("common_hour")}
                </label>
                <select
                  value={form.dailyHour}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, dailyHour: e.target.value }))
                  }
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  {HOURS.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-sm font-semibold">
                  {t("common_minute")}
                </label>
                <select
                  value={form.dailyMinute}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, dailyMinute: e.target.value }))
                  }
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  {MINUTES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {form.scheduleType === "weekly_window" && (
            <div className="space-y-2 border-t border-slate-200 pt-2">
              <div className="flex gap-2">
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-sm font-semibold">
                    {t("common_hour")}
                  </label>
                  <select
                    value={form.weekHour}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, weekHour: e.target.value }))
                    }
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    {HOURS.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-sm font-semibold">
                    {t("schedule_minute_start_label")}
                  </label>
                  <select
                    value={form.minuteStart}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, minuteStart: e.target.value }))
                    }
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    {MINUTES.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1 flex-1">
                  <label className="text-sm font-semibold">
                    {t("schedule_minute_end_label")}
                  </label>
                  <select
                    value={form.minuteEnd}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, minuteEnd: e.target.value }))
                    }
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    {MINUTES.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold block mb-1">
                  {t("lotto_service_mode_days_of_week")}
                </label>
                <div className="flex flex-wrap gap-1">
                  {WEEKDAY_LABEL_KEYS.map((labelKey, dayIndex) => (
                    <button
                      type="button"
                      key={dayIndex}
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          daysOfWeek: toggleInArray(f.daysOfWeek, dayIndex),
                        }))
                      }
                      className={`text-[11px] rounded px-2 py-1 ${
                        form.daysOfWeek.includes(dayIndex)
                          ? "bg-brand-blue-600 text-white"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      {t(labelKey)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {form.scheduleType === "advanced" && (
            <div className="flex flex-col gap-1 border-t border-slate-200 pt-2">
              <label className="text-sm font-semibold">Cron Expression</label>
              <input
                type="text"
                value={form.cronExpression}
                onChange={(e) =>
                  setForm((f) => ({ ...f, cronExpression: e.target.value }))
                }
                placeholder={t("schedule_cron_placeholder")}
                className="border border-gray-300 rounded px-2 py-1 text-sm font-mono"
              />
            </div>
          )}

          <div className="flex items-center gap-2 border-t border-slate-200 pt-2">
            <Switch
              checked={form.enabled}
              onChange={(e) =>
                setForm((f) => ({ ...f, enabled: e.target.checked }))
              }
              inputProps={{ "aria-label": "controlled" }}
            />
            <span className="text-sm">{t("schedule_enabled_label")}</span>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={closeDialog}
              className="text-sm font-bold px-3 py-1 rounded border border-slate-300"
            >
              {t("common_cancel")}
            </button>
            <button
              onClick={handleSubmit}
              className="text-sm font-bold px-3 py-1 rounded bg-brand-blue-600 hover:bg-brand-blue-700 text-white"
            >
              {t("membertable_save")}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export default Schedule;
